<script setup>
import { computed, defineProps, onMounted, ref } from 'vue'; // Added defineProps and onMounted here
import { useRoute } from 'vue-router'; // Assuming vue-router is used for route parameters
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api } from '../services/api';

const props = defineProps({
    navigate: {
        type: Function,
        required: true
    }
});

console.log('Props received:', props);

// Dummy blog posts data (should ideally come from an API)


import { useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const postId = computed(() => parseInt(route.params.postId));
const postData = ref(null); // Initialize with null
const isLoading = ref(false); // New reactive state for loading

async function getBlogPost(id) { // Changed parameter name to avoid confusion with ref
    isLoading.value = true; // Set loading to true before fetching
    try {
        postData.value = await api.getPost(id); // Await the JSON parsing
    } catch(error) {
        console.error("Data is unable to load at this moment try again later:", error);
    } finally {
        isLoading.value = false; // Set loading to false after fetch completes
    }
}

onMounted(() => {
    console.log('onMounted hook executed.'); // Log onMounted execution
    // Use .value to pass the actual ID to the function
    if (postId.value) {
        getBlogPost(postId.value);
    }
});

const formattedDate = computed(() => {
    if (postData.value && postData.value.created_at) {
        return new Date(postData.value.created_at).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }
    return '';
});

const sanitizedMarkdown = computed(() => {
    if (postData.value && postData.value.blog_body) {
        // 1. Convert Markdown to HTML
        const rawHtml = marked(postData.value.blog_body);
        // 2. Sanitize the HTML to prevent XSS attacks
        return DOMPurify.sanitize(rawHtml);
    }
    return '';
});
</script>

<template>
    <div class="bg-gray-50 font-sans leading-normal tracking-tight">
        <div class="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <!-- Loading Skeleton -->
            <div v-if="isLoading" class="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
                <div class="w-full h-64 sm:h-80 md:h-96 bg-gray-300"></div>
                <div class="p-6 sm:p-8 md:p-12">
                    <div class="h-6 bg-gray-300 rounded w-1/4 mb-8"></div>
                    <div class="space-y-4">
                        <div class="h-4 bg-gray-300 rounded w-full"></div>
                        <div class="h-4 bg-gray-300 rounded w-5/6"></div>
                        <div class="h-4 bg-gray-300 rounded w-full"></div>
                        <div class="h-4 bg-gray-300 rounded w-3/4"></div>
                    </div>
                </div>
            </div>

            <!-- Blog Post Content -->
            <article v-else-if="postData" class="bg-white rounded-lg shadow-lg overflow-hidden">
                 <header class="relative">
                    <img :src="postData.img_path" :alt="postData.title" class="w-full h-64 sm:h-80 md:h-96 object-cover"/>
                    <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div class="absolute bottom-0 left-0 p-6 sm:p-8 md:p-10">
                        <h1 class="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight break-words">{{ postData.title }}</h1>
                    </div>
                </header>

                <div class="p-6 sm:p-8 md:p-12">
                    <div class="flex items-center text-gray-500 text-base mb-8">
                        <div class="flex items-center">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                            <span>Published on {{ formattedDate }}</span>
                        </div>
                    </div>

                    <div class="prose max-w-none text-lg text-gray-700" v-html="sanitizedMarkdown"></div>
                    
                    <div class="mt-12 pt-8 border-t border-gray-200">
                      <button @click="router.push({ name: 'blog' })" class="inline-flex items-center bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-dark transition-colors duration-300 transform hover:-translate-y-1 shadow-md hover:shadow-lg">
                          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                          Back to Blog
                      </button>
                    </div>
                </div>
            </article>

            <!-- Error or No Data Message -->
            <div v-else class="text-center py-20">
                <p class="text-xl text-gray-600">Could not load the blog post. Please try again later.</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bg-primary { background-color: #D62828; }
.text-primary { color: #D62828; }
.bg-primary-dark { background-color: #b02020; }
.hover\:bg-primary-dark:hover { background-color: #b02020; }

/* Using a mobile-first approach. Base styles are for mobile. */

/* Enhanced prose styling for better readability and a modern look */
.prose {
    color: #374151; /* gray-700 */
    line-height: 1.7;
    font-size: 1rem; /* Base font size for mobile */
}

/* Medium screens and up (tablets) */
@media (min-width: 768px) {
    .prose {
        font-size: 1.125rem; /* Larger font for tablets */
    }
}

.prose h2, .prose h3 {
    font-weight: 800; /* extrabold */
    color: #1f2937; /* gray-800 */
    margin-top: 2em;
    margin-bottom: 1em;
    padding-bottom: 0.3em;
    border-bottom: 2px solid #e5e7eb; /* gray-200 */
}

.prose h2 {
    font-size: 1.5rem; /* 2xl on mobile */
}

.prose h3 {
    font-size: 1.25rem; /* xl on mobile */
}

/* Medium screens and up */
@media (min-width: 768px) {
    .prose h2 {
        font-size: 1.875rem; /* 3xl */
    }

    .prose h3 {
        font-size: 1.5rem; /* 2xl */
    }
}

.prose p {
    margin-bottom: 1.25em;
}

.prose strong {
    color: #111827; /* gray-900 */
}

.prose a {
    color: #D62828; /* primary color */
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;
}

.prose a:hover {
    color: #b02020; /* primary-dark */
    text-decoration: underline;
}

.prose blockquote {
    border-left: 4px solid #D62828;
    padding-left: 1rem;
    margin-left: 0;
    font-style: italic;
    color: #4b5563; /* gray-600 */
}

.prose ul, .prose ol {
    margin-left: 1.5rem;
    margin-bottom: 1.25em;
}

.prose li {
    margin-bottom: 0.5em;
}

.prose code {
    background-color: #f3f4f6; /* gray-100 */
    color: #1f2937; /* gray-800 */
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 85%;
}

.prose pre {
    background: #1f2937; /* gray-800 */
    color: #f9fafb; /* gray-50 */
    padding: 1.25em;
    border-radius: 0.5rem; /* lg */
    overflow-x: auto;
}
</style>
